import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import Section from '../../components/Section'
import workHistoryApi from '../../api/workHistory/WorkHistoryApi'
import companyApi from '../../api/company/CompanyApi'
import {
    CompanyEntityFilter,
    WorkHistoryCsSearchResultDto,
} from '../../api/workHistory/models/WorkHistoryCsDtos'
import { CompanyCsSearchResultDto } from '../../api/company/models/companyCs/CompanyCsDtos'
import Dropdown, {
    Item as DropdownItem,
} from '../../uiV2/common/form/inputs/DropDown'
import ProfilePic from '../../uiV2/common/person/ProfilePic'
import { formatPersonName } from '../../utils/personUtils'
import LinkedInLink from '../LinkedInLink'
import Button from '../../uiV2/common/form/inputs/Button'
import LoadingSpinner from '../../uiV2/common/loading/LoadingSpinner'
import Badge from '../../uiV2/common/badge/Badge'
import { BadgeColor } from '../../uiV2/common/badge/badgeTypes'
import WorkHistoryMergePreviewModal from './WorkHistoryMergePreviewModal'
import Modal from '../../uiV2/common/modals/Modal'

const DEBOUNCE_MS = 300

const COMPANY_ENTITY_FILTER_PARAM = 'companyEntityFilter'
const COMPANY_ENTITY_ITEMS: DropdownItem[] = [
    { id: 'all', content: 'All' },
    { id: 'hasDataProviderId', content: 'Has Data Provider Id' },
    { id: 'noDataProviderId', content: 'No Data Provider Id' },
]

const parseCompanyEntityFilter = (
    value: string | null,
): CompanyEntityFilter => {
    if (value === 'hasDataProviderId') return 'hasDataProviderId'
    if (value === 'noDataProviderId') return 'noDataProviderId'
    return 'all'
}

const formatDate = (value?: string | null) => {
    if (!value) return 'Present'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
    }).format(date)
}

export default function WorkHistoryColdStorage(): React.JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams()
    const [query, setQuery] = useState<string>(
        () => searchParams.get('workHistorySearch') || '',
    )
    const [results, setResults] = useState<WorkHistoryCsSearchResultDto[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [pageNumber, setPageNumber] = useState(1)
    const [perPage, setPerPage] = useState(100)
    const [hasMore, setHasMore] = useState(false)
    const [companyEntityFilter, setCompanyEntityFilter] =
        useState<CompanyEntityFilter>(() =>
            parseCompanyEntityFilter(
                searchParams.get(COMPANY_ENTITY_FILTER_PARAM),
            ),
        )
    const [selectedResults, setSelectedResults] = useState<
        Record<string, WorkHistoryCsSearchResultDto>
    >({})
    const [mergeError, setMergeError] = useState<string | undefined>()
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false)
    const [targetCompany, setTargetCompany] = useState<
        CompanyCsSearchResultDto | undefined
    >()
    const [mergeModalError, setMergeModalError] = useState<string | undefined>()
    const [mergeLoading, setMergeLoading] = useState(false)
    const [mergeSuccessMessage, setMergeSuccessMessage] = useState<
        string | undefined
    >()
    const [reloadToken, setReloadToken] = useState(0)
    const [alwaysMergeSelected, setAlwaysMergeSelected] = useState(false)
    const [alwaysMergeInfoOpen, setAlwaysMergeInfoOpen] = useState(false)
    const [alwaysMergeInfoLoading, setAlwaysMergeInfoLoading] = useState(false)
    const [alwaysMergeInfoError, setAlwaysMergeInfoError] = useState<
        string | undefined
    >()
    const [alwaysMergeAdditionalCount, setAlwaysMergeAdditionalCount] =
        useState(0)
    const alwaysMergeRequestTokenRef = useRef(0)

    const closeMergeModal = useCallback(() => {
        setIsMergeModalOpen(false)
        setTargetCompany(undefined)
        setMergeModalError(undefined)
        setMergeLoading(false)
        setAlwaysMergeSelected(false)
        setAlwaysMergeInfoOpen(false)
        setAlwaysMergeInfoLoading(false)
        setAlwaysMergeInfoError(undefined)
        setAlwaysMergeAdditionalCount(0)
        alwaysMergeRequestTokenRef.current += 1
    }, [])

    const trimmedQuery = useMemo(() => query.trim(), [query])
    const companyEntityDropdownItem = useMemo(() => {
        return (
            COMPANY_ENTITY_ITEMS.find(
                (item) => item.id === companyEntityFilter,
            ) || COMPANY_ENTITY_ITEMS[0]
        )
    }, [companyEntityFilter])
    const selectedEntries = useMemo(
        () => Object.values(selectedResults),
        [selectedResults],
    )
    const allCurrentSelected = useMemo(
        () =>
            results.length > 0 &&
            results.every((result) => Boolean(selectedResults[result.id])),
        [results, selectedResults],
    )
    const selectedCount = selectedEntries.length

    const targetCompanyId = targetCompany?.id
    const sourceCompanyIds = useMemo(() => {
        const ids = new Set<string>()
        selectedEntries.forEach((entry) => {
            const companyId = entry.companyId
            if (!companyId) return
            if (targetCompanyId && companyId === targetCompanyId) return
            ids.add(companyId)
        })
        return Array.from(ids)
    }, [selectedEntries, targetCompanyId])
    const selectedSourceCount = sourceCompanyIds.length
    const totalCompanyMergeCount =
        selectedSourceCount + alwaysMergeAdditionalCount

    useEffect(() => {
        const urlQuery = searchParams.get('workHistorySearch') || ''
        setQuery((prev) => (prev !== urlQuery ? urlQuery : prev))
        const urlFilter = parseCompanyEntityFilter(
            searchParams.get(COMPANY_ENTITY_FILTER_PARAM),
        )
        setCompanyEntityFilter((prev) =>
            prev !== urlFilter ? urlFilter : prev,
        )
    }, [searchParams])

    useEffect(() => {
        const current = searchParams.toString()
        const next = new URLSearchParams(searchParams)
        if (query) {
            next.set('workHistorySearch', query)
        } else {
            next.delete('workHistorySearch')
        }
        if (companyEntityFilter && companyEntityFilter !== 'all') {
            next.set(COMPANY_ENTITY_FILTER_PARAM, companyEntityFilter)
        } else {
            next.delete(COMPANY_ENTITY_FILTER_PARAM)
        }
        next.delete('workHistorySearchType')
        if (next.toString() !== current) {
            setSearchParams(next, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, companyEntityFilter])

    useEffect(() => {
        setPageNumber(1)
    }, [trimmedQuery, companyEntityFilter])

    useEffect(() => {
        setSelectedResults({})
        setMergeError(undefined)
        closeMergeModal()
    }, [trimmedQuery, companyEntityFilter, closeMergeModal])

    useEffect(() => {
        if (!alwaysMergeSelected) {
            alwaysMergeRequestTokenRef.current += 1
            setAlwaysMergeInfoOpen(false)
            setAlwaysMergeInfoLoading(false)
            setAlwaysMergeInfoError(undefined)
            setAlwaysMergeAdditionalCount(0)
            return
        }

        const currentSourceIds = sourceCompanyIds
        const currentTargetId = targetCompanyId
        const token = alwaysMergeRequestTokenRef.current + 1
        alwaysMergeRequestTokenRef.current = token
        setAlwaysMergeInfoError(undefined)

        if (currentSourceIds.length === 0) {
            setAlwaysMergeAdditionalCount(0)
            setAlwaysMergeInfoLoading(false)
            return
        }

        setAlwaysMergeInfoLoading(true)

        void (async () => {
            try {
                const res = await companyApi.findMatchingCompanyCs({
                    companyIds: currentSourceIds,
                })
                if (alwaysMergeRequestTokenRef.current !== token) return

                const uniqueAdditionalIds = new Set(
                    res.companies.map((company) => company.id),
                )
                currentSourceIds.forEach((id) => uniqueAdditionalIds.delete(id))
                if (currentTargetId) {
                    uniqueAdditionalIds.delete(currentTargetId)
                }

                setAlwaysMergeAdditionalCount(uniqueAdditionalIds.size)
                setAlwaysMergeInfoError(undefined)
            } catch (error) {
                if (alwaysMergeRequestTokenRef.current !== token) return

                const message =
                    (error as Error)?.message ||
                    'Failed to fetch matching companies.'
                setAlwaysMergeInfoError(message)
                setAlwaysMergeAdditionalCount(0)
            } finally {
                if (alwaysMergeRequestTokenRef.current === token) {
                    setAlwaysMergeInfoLoading(false)
                }
            }
        })()
    }, [alwaysMergeSelected, sourceCompanyIds, targetCompanyId])

    useEffect(() => {
        let ignore = false
        const handle = window.setTimeout(async () => {
            try {
                setLoading(true)
                const res = await workHistoryApi.searchWorkHistoryCs({
                    q: trimmedQuery || undefined,
                    pageNumber,
                    perPage,
                    companyEntityFilter,
                })
                if (ignore) return
                setResults(res.results)
                setPerPage(res.perPage)
                setHasMore(res.hasMore)
                setError(undefined)
            } catch (e: any) {
                if (!ignore) {
                    setError(
                        e?.message || 'Failed to fetch work history records',
                    )
                }
            } finally {
                if (!ignore) {
                    setLoading(false)
                }
            }
        }, DEBOUNCE_MS)
        return () => {
            ignore = true
            window.clearTimeout(handle)
        }
    }, [trimmedQuery, pageNumber, perPage, companyEntityFilter, reloadToken])

    const buildTabSearch = (
        tabSlug: string,
        extraParams: Record<string, string | undefined>,
    ) => {
        const params = new URLSearchParams()
        params.set('tab', tabSlug)
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value) params.set(key, value)
        })
        const search = params.toString()
        return { pathname: '', search: search ? `?${search}` : '' }
    }

    const onPrev = () => {
        setPageNumber((prev) => Math.max(1, prev - 1))
    }

    const onNext = () => {
        setPageNumber((prev) => prev + 1)
    }

    const onToggleSelect = (item: WorkHistoryCsSearchResultDto) => {
        setSelectedResults((prev) => {
            const next = { ...prev }
            if (next[item.id]) {
                delete next[item.id]
            } else {
                next[item.id] = item
            }
            return next
        })
        setMergeError(undefined)
    }

    const onToggleSelectAll = () => {
        setSelectedResults((prev) => {
            const next = { ...prev }
            if (allCurrentSelected) {
                results.forEach((result) => {
                    delete next[result.id]
                })
            } else {
                results.forEach((result) => {
                    next[result.id] = result
                })
            }
            return next
        })
        setMergeError(undefined)
    }

    const onMergeCompanies = () => {
        if (selectedCount === 0) {
            setMergeError('Select at least one company to merge.')
            setMergeSuccessMessage(undefined)
            return
        }

        const invalidSelections = selectedEntries.filter(
            (entry) => entry.companyDataProviderId,
        )

        if (invalidSelections.length > 0) {
            const details = invalidSelections
                .map((entry) => {
                    const companyDisplay = entry.companyName || 'Unknown'
                    return `${companyDisplay} (${entry.companyId})`
                })
                .join(', ')
            setMergeError(
                `Cannot merge companies that have a data provider id: ${details}.`,
            )
            setMergeSuccessMessage(undefined)
            closeMergeModal()
            return
        }

        setMergeError(undefined)
        setMergeModalError(undefined)
        setMergeLoading(false)
        setAlwaysMergeSelected(false)
        setIsMergeModalOpen(true)
    }

    const handleMergeConfirm = useCallback(async () => {
        if (mergeLoading) return

        if (!targetCompany) {
            setMergeModalError('Select a target company to merge into.')
            return
        }

        if (sourceCompanyIds.length === 0) {
            setMergeModalError(
                'Select at least one source company different to the target.',
            )
            return
        }

        setMergeLoading(true)
        setMergeModalError(undefined)

        try {
            const result = await companyApi.mergeCompanyCs({
                sourceCompanyIds,
                targetCompanyId: targetCompany.id,
                createMergeOverrides: alwaysMergeSelected,
            })

            setMergeSuccessMessage(
                `Merged ${result.mergeCount} companies and updated ${result.updatedWorkHistoryCount} work histories.`,
            )
            setSelectedResults({})
            setMergeError(undefined)
            setReloadToken((prev) => prev + 1)
            closeMergeModal()
        } catch (error) {
            const message =
                (error as Error)?.message || 'Failed to merge companies.'
            setMergeModalError(message)
        } finally {
            setMergeLoading(false)
        }
    }, [
        alwaysMergeSelected,
        closeMergeModal,
        mergeLoading,
        targetCompany,
        sourceCompanyIds,
    ])

    const handleAlwaysMergeChange = useCallback((value: boolean) => {
        setAlwaysMergeSelected(value)
        if (value) {
            setAlwaysMergeInfoOpen(true)
            setAlwaysMergeInfoLoading(true)
            setAlwaysMergeInfoError(undefined)
            setAlwaysMergeAdditionalCount(0)
        } else {
            setAlwaysMergeInfoOpen(false)
            setAlwaysMergeInfoLoading(false)
        }
    }, [])

    const handleAlwaysMergeCancel = useCallback(() => {
        setAlwaysMergeSelected(false)
        setAlwaysMergeInfoOpen(false)
    }, [])

    return (
        <div className="mt-6">
            <Section subtitle="Work History Cold Storage">
                <div className="flex flex-col gap-3">
                    <input
                        className="form-input"
                        placeholder="Search by work history ID, person ID, or company ID"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-3">
                        <div className="w-full max-w-xs">
                            <Dropdown
                                id="company-entity-filter"
                                name="Company Entity"
                                placeholder="Select company entity filter"
                                items={COMPANY_ENTITY_ITEMS}
                                selected={companyEntityDropdownItem}
                                onChange={(item) =>
                                    setCompanyEntityFilter(
                                        parseCompanyEntityFilter(item.id),
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            type="button"
                            style="secondary"
                            onClick={onToggleSelectAll}
                            enabled={results.length > 0}
                        >
                            {allCurrentSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                        <Button
                            type="button"
                            style="primary"
                            onClick={onMergeCompanies}
                            enabled={selectedCount > 0}
                        >
                            Merge Companies
                        </Button>
                        {selectedCount > 0 && (
                            <span className="text-sm text-gray-600">
                                {selectedCount} selected
                            </span>
                        )}
                    </div>
                    {loading && (
                        <div className="py-4">
                            <LoadingSpinner
                                spinnerSize={48}
                                captions={[
                                    {
                                        caption: trimmedQuery
                                            ? 'Searching work history…'
                                            : 'Loading work history…',
                                    },
                                ]}
                                className="h-auto w-full"
                            />
                        </div>
                    )}
                    {error && <div className="text-red-600">{error}</div>}
                    {mergeError && (
                        <div className="text-red-600 text-sm">{mergeError}</div>
                    )}
                    {mergeSuccessMessage && (
                        <div className="text-green-600 text-sm">
                            {mergeSuccessMessage}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="main-table w-full">
                            <thead>
                                <tr>
                                    <th className="w-12 text-center">Select</th>
                                    <th className="w-12">Pic</th>
                                    <th>Person</th>
                                    <th>Person ID</th>
                                    <th>Person LinkedIn</th>
                                    <th>Work History ID</th>
                                    <th>Company</th>
                                    <th>Company ID</th>
                                    <th>Company Data Provider ID</th>
                                    <th>Company LinkedIn</th>
                                    <th>Job Title</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r) => {
                                    const name = formatPersonName({
                                        firstName: r.personFirstName,
                                        middleName: r.personMiddleName,
                                        lastName: r.personLastName,
                                    })
                                    const personNameDisplay = name || '-'
                                    const companyNameDisplay =
                                        r.companyName || '-'
                                    const isSelected = Boolean(
                                        selectedResults[r.id],
                                    )
                                    return (
                                        <tr
                                            key={r.id}
                                            className={
                                                isSelected
                                                    ? 'bg-sky-50'
                                                    : undefined
                                            }
                                        >
                                            <td className="w-12 text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox h-4 w-4"
                                                    checked={isSelected}
                                                    onChange={() =>
                                                        onToggleSelect(r)
                                                    }
                                                    aria-label={`Select company ${companyNameDisplay}`}
                                                />
                                            </td>
                                            <td className="w-12">
                                                <ProfilePic
                                                    src={
                                                        r.personLinkedInProfilePicUrl
                                                    }
                                                    size={32}
                                                    alt={personNameDisplay}
                                                />
                                            </td>
                                            <td>{personNameDisplay}</td>
                                            <td className="text-xs text-gray-700">
                                                {r.personId ? (
                                                    <Link
                                                        to={buildTabSearch(
                                                            'person-cold-storage',
                                                            {
                                                                personSearch:
                                                                    r.personId,
                                                                personSearchMode:
                                                                    'id',
                                                            },
                                                        )}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {r.personId}
                                                    </Link>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td>
                                                {r.personLinkedInUrl ? (
                                                    <LinkedInLink
                                                        url={
                                                            r.personLinkedInUrl
                                                        }
                                                        ariaLabel={`Open LinkedIn profile for ${personNameDisplay}`}
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-xs text-gray-700">
                                                {r.id}
                                            </td>
                                            <td>{companyNameDisplay}</td>
                                            <td className="text-xs text-gray-700">
                                                {r.companyId ? (
                                                    <Link
                                                        to={buildTabSearch(
                                                            'company-cold-storage',
                                                            {
                                                                companyColdStorageSearch:
                                                                    r.companyId,
                                                                companySearchMode:
                                                                    'id',
                                                            },
                                                        )}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {r.companyId}
                                                    </Link>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="text-xs text-gray-700">
                                                {r.companyDataProviderId || '-'}
                                            </td>
                                            <td>
                                                {r.companyLinkedInUrl ? (
                                                    <LinkedInLink
                                                        url={
                                                            r.companyLinkedInUrl
                                                        }
                                                        ariaLabel={`Open LinkedIn page for ${companyNameDisplay}`}
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td>{r.jobTitle || '-'}</td>
                                            <td className="whitespace-nowrap">
                                                <span>
                                                    {formatDate(r.startDate)}
                                                </span>
                                                {r.assumedStartDate && (
                                                    <span
                                                        className="ml-2 inline-flex items-center text-warning-700"
                                                        title="Missing start date, 1 year ago has been assumed"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faTriangleExclamation
                                                            }
                                                        />
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap">
                                                {r.endDate ? (
                                                    formatDate(r.endDate)
                                                ) : (
                                                    <Badge
                                                        color={
                                                            BadgeColor.BrandBlue
                                                        }
                                                    >
                                                        current
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                                {results.length === 0 && !loading && (
                                    <tr>
                                        <td
                                            colSpan={13}
                                            className="text-center py-6"
                                        >
                                            No results
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between items-center mt-2">
                        <Button
                            type="button"
                            style="secondary"
                            onClick={onPrev}
                            enabled={pageNumber > 1 && !loading}
                        >
                            Previous
                        </Button>
                        <div className="text-sm text-gray-600">
                            Page {pageNumber} · Showing {results.length} · Per
                            page {perPage}
                        </div>
                        <Button
                            type="button"
                            style="secondary"
                            onClick={onNext}
                            enabled={!loading && hasMore}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </Section>
            {isMergeModalOpen && (
                <WorkHistoryMergePreviewModal
                    companies={selectedEntries}
                    targetCompany={targetCompany}
                    onTargetCompanySelect={(company) => {
                        setTargetCompany(company)
                        setMergeModalError(undefined)
                    }}
                    onTargetCompanyClear={() => {
                        setTargetCompany(undefined)
                        setMergeModalError(undefined)
                        setAlwaysMergeSelected(false)
                    }}
                    onMerge={handleMergeConfirm}
                    mergeLoading={mergeLoading}
                    canMerge={Boolean(
                        targetCompany && selectedEntries.length > 0,
                    )}
                    errorMessage={mergeModalError}
                    onClose={closeMergeModal}
                    alwaysMergeSelected={alwaysMergeSelected}
                    onAlwaysMergeChange={handleAlwaysMergeChange}
                />
            )}
            {alwaysMergeInfoOpen && (
                <Modal
                    title="Always Merge These Company Names"
                    onClose={() => setAlwaysMergeInfoOpen(false)}
                    size="medium"
                    allowUserToClose={!alwaysMergeInfoLoading}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={handleAlwaysMergeCancel}
                                enabled={!alwaysMergeInfoLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={() => setAlwaysMergeInfoOpen(false)}
                                enabled={!alwaysMergeInfoLoading}
                            >
                                OK
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4 text-sm text-gray-700">
                        <p>
                            This will also merge any other existing companies
                            with the source names into the target company.
                        </p>
                        {alwaysMergeInfoLoading ? (
                            <div className="flex items-center gap-3 text-gray-600">
                                <LoadingSpinner spinnerSize={24} />
                                <span>Checking for matching companies…</span>
                            </div>
                        ) : alwaysMergeInfoError ? (
                            <div className="text-red-600">
                                {alwaysMergeInfoError}
                            </div>
                        ) : (
                            <div className="space-y-2 text-sm text-gray-900">
                                <p className="font-semibold">
                                    ADDITIONAL company count that will be
                                    merged:{' '}
                                    <span className="font-normal text-gray-700">
                                        {alwaysMergeAdditionalCount}
                                    </span>
                                </p>
                                <p className="font-semibold">
                                    Total company merge count:{' '}
                                    <span className="font-normal text-gray-700">
                                        {totalCompanyMergeCount}
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    )
}
